import type { components } from '@multaqa/api-client'
import { useLocale } from '@multaqa/i18n'
import {
  type UseQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { api } from '@/lib/api'

/**
 * Company-scoped audit reads. Filters are server-side and pages are cursor based.
 */

export type AuditLogResource = components['schemas']['AuditEventResource']
export type AuditFacets = components['schemas']['AuditFacetsResource']
export type AuditPageMeta = components['schemas']['AuditPageMetaResource']
export type PlatformSupportSessionResource = components['schemas']['PlatformSupportSessionResource']

/** Filters stored in the route search and sent to the server. */
export interface AuditFilters {
  q?: string
  actor_id?: string
  /** ADR-0033: every row one support session wrote ("View actions"). */
  support_session_id?: string
  module?: string
  entity_type?: string
  entity_id?: string
  action_kind?: 'created' | 'changed' | 'deleted' | 'other'
  from?: string
  to?: string
}

export function useCompanyAudit(filters: AuditFilters = {}, options?: { enabled?: boolean }) {
  // The API translates the labels, so each language is its own cache entry.
  const { locale } = useLocale()
  return useInfiniteQuery({
    queryKey: ['audit', 'company', locale, filters] as const,
    enabled: options?.enabled ?? true,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }): Promise<{ rows: AuditLogResource[]; meta: AuditPageMeta }> => {
      const response = await api.GET('/audit', {
        params: { query: { ...filters, cursor: pageParam, per_page: 50 } },
      })
      if (response.error) throw response.error
      return { rows: response.data.data, meta: response.data.meta }
    },
    getNextPageParam: (lastPage) => lastPage.meta.next_cursor ?? undefined,
  })
}

export function useAuditFacets() {
  return useQuery({
    queryKey: ['audit', 'facets'] as const,
    queryFn: async (): Promise<AuditFacets> => {
      const response = await api.GET('/audit/facets')
      if (response.error) throw response.error
      return response.data.data
    },
  })
}

export async function exportCompanyAudit(filters: AuditFilters): Promise<void> {
  const response = await api.GET('/audit/export', { params: { query: filters } })
  if (response.error) throw response.error
  const file = new Blob([response.data as BlobPart], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(file)
  const link = document.createElement('a')
  link.href = url
  link.download = 'audit-trail.csv'
  link.click()
  URL.revokeObjectURL(url)
}

/** FR-AUD-005: the sessions platform support opened into this group. */
export function useSupportSessions(
  options?: Omit<UseQueryOptions<PlatformSupportSessionResource[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<PlatformSupportSessionResource[], Error>({
    queryKey: ['support-sessions'] as const,
    ...options,
    queryFn: async (): Promise<PlatformSupportSessionResource[]> => {
      const { data } = await api.GET('/support-sessions')
      if (!data) {
        throw new Error('GET /support-sessions returned no body')
      }
      return data.data
    },
  })
}

/** ADR-0033: an owner stops a support session. The server checks ownership. */
export function useEndSupportSession() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (sessionId: string) =>
      api.POST('/support-sessions/{session}/end', { params: { path: { session: sessionId } } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['support-sessions'] })
    },
  })
}

export type SupportSessionStatus = 'active' | 'revoked' | 'expired'

export function sessionStatus(
  session: PlatformSupportSessionResource,
  now: Date = new Date(),
): SupportSessionStatus {
  if (session.revoked_at !== null) {
    return 'revoked'
  }
  if (new Date(session.expires_at).getTime() <= now.getTime()) {
    return 'expired'
  }
  return 'active'
}
